import React, { MouseEventHandler, ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  EuiBreadcrumb,
  EuiButtonEmpty,
  EuiButtonIcon,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHeaderBreadcrumbs,
  EuiHorizontalRule,
  EuiIcon,
  EuiLink,
  EuiPage,
  EuiPageBody,
  EuiPageContent,
  EuiPageContentBody,
  EuiPageHeader,
  EuiProvider,
  EuiSpacer,
  EuiText,
  EuiGlobalToastList,
  IconType,
} from '@elastic/eui';
import { useLocalStorage } from '../hooks';
import {
  AlertType,
  CURRENT_SETTINGS_VERSION,
  DEFAULT_DOMICILE_CODE,
  deserializeNotification,
  getDomicileLabel,
  Notification,
  NotificationStatus,
  Parameters,
  SerializedNotification,
  SerializedParameters,
  Settings,
  settingsDefault,
  settingsSetNotificationStatuses,
  upgradeSettings,
} from '../model';
import { ContactFormModal } from './contact_form_modal';
import { DomicileSelectorModal } from './domicile_selector_modal';
import { PageContext } from './page_context';
import { NotificationsFlyout } from './notifications_flyout';
import { SettingsFlyout } from './settings_flyout';
import { PageErrorState } from '../components';
import { Toast } from '@elastic/eui/src/components/toast/global_toast_list';
import { SunsetModal } from '../components/sunset_modal';

export interface PageContainerProps {
  domicile?: string;
}

export interface PageToast {
  id: string;
  title?: ReactNode;
  text?: ReactElement;
  iconType?: IconType;
  color?: 'primary' | 'success' | 'warning' | 'danger';
}

export function PageContainer({ domicile }: PageContainerProps) {
  const [settings, setSettings] = useLocalStorage<Settings>('settings', settingsDefault());
  if (settings?.version !== CURRENT_SETTINGS_VERSION) {
    setSettings(upgradeSettings(settings));
  }

  if (settings.theme == 'dark') {
    import('@elastic/eui/dist/eui_theme_dark.min.css');
  }

  const [toasts, setToasts] = useState<PageToast[]>([]);
  const addToast = useCallback(
    (toast: PageToast) => {
      setToasts([...toasts, toast]);
    },
    [toasts],
  );
  const removeToast = useCallback(
    (removedToast: Toast) => {
      setToasts(toasts.filter((toast) => toast.id !== removedToast.id));
    },
    [toasts],
  );

  const navigate = useNavigate();
  const location = useLocation();
  const domicileCode = domicile ?? settings.country ?? DEFAULT_DOMICILE_CODE;
  const getURL = useCallback(
    (path: string) => (domicileCode !== DEFAULT_DOMICILE_CODE ? `/${domicileCode}${path}` : path),
    [domicileCode],
  );

  useEffect(() => {
    // If page is loaded without domicile in URL, but settings are configured for non-default domicile, we should update
    // current URL.
    if (settings.country !== DEFAULT_DOMICILE_CODE && !domicile) {
      navigate(
        {
          pathname: `/${settings.country}${location.pathname}`,
          hash: location.hash,
          search: location.search,
        },
        { replace: true },
      );
    }
  }, []);

  const [parameters, setParameters] = useState<Parameters>({
    synced: false,
    status: { level: 'available' },
    domicileCode,
    domiciles: [],
    providers: new Map(),
    fundAssetClasses: new Map(),
    assetClasses: new Map(),
    notices: [],
    license: {
      maxFundsToAnalyze: Infinity,
      maxAssetsToAnalyze: Infinity,
      maxAlerts: Infinity,
    },
  });
  useEffect(() => {
    // Don't reload parameters if domicile hasn't changed.
    if (parameters.synced && domicileCode === parameters.domicileCode) {
      return;
    }

    axios.get(`/api/parameters/${encodeURIComponent(domicileCode)}`).then(
      ({ data }: { data: SerializedParameters }) => {
        setParameters({
          synced: true,
          status: data.status,
          domicileCode: data.domicileCode,
          domiciles: data.domiciles,
          providers: new Map(data.providers.map((providerType) => [providerType.type, providerType])),
          fundAssetClasses: new Map(
            data.fundAssetClasses.map((assetClassType) => [assetClassType.type, assetClassType]),
          ),
          assetClasses: new Map(data.assetClasses.map((assetClassType) => [assetClassType.type, assetClassType])),
          notices: data.notices,
          license: data.license,
        });
      },
      () => {
        setParameters({ ...parameters, status: { level: 'unavailable' }, synced: true });
      },
    );
  }, [domicileCode, settings]);

  const [notifications, setNotifications] = useState<{ notifications: Notification[]; synced: boolean }>({
    notifications: [],
    synced: false,
  });
  useEffect(() => {
    if (notifications.synced || !parameters.synced) {
      return;
    }

    const alerts: Array<{ timestamp: string; rule: { type: string; value: string } }> = [];
    for (const alert of settings.alerts ?? []) {
      if (alert.type === AlertType.AssetIsAddedToFund || alert.type === AlertType.AssetIsRemovedFromFund) {
        alerts.push({
          timestamp: alert.timestamp,
          rule: { type: alert.type.toString(), value: alert.assetSourceId },
        });
      }
    }

    axios
      .post('/api/notifications/check', {
        news: { include: true },
        alerts: alerts.slice(0, parameters.license.maxAlerts),
      })
      .then(
        ({ data }: { data: SerializedNotification[] }) => {
          // Clean statuses
          if (settings.notifications) {
            const knownIds = new Set(data.map((serializedNotification) => serializedNotification.i));
            const updatedStatuses = settings.notifications.statuses.filter((status) => knownIds.has(status.id));
            if (updatedStatuses.length !== settings.notifications.statuses.length) {
              setSettings(settingsSetNotificationStatuses(settings, updatedStatuses));
            }
          }

          setNotifications({
            synced: true,
            notifications: data.map((serializedNotification) => deserializeNotification(serializedNotification)),
          });
        },
        () => setNotifications({ synced: false, notifications: [] }),
      );
  }, [settings, parameters]);

  const notificationStatuses = useMemo<Map<number, NotificationStatus>>(
    () => new Map(settings.notifications?.statuses.map((status) => [status.id, status]) ?? []),
    [settings],
  );

  const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(false);
  const onToggleSettings = useCallback(() => {
    setIsSettingsOpen(!isSettingsOpen);
  }, [isSettingsOpen]);

  const [isNotificationsOpen, setIsNotificationsOpen] = useState<boolean>(false);
  const onToggleNotifications = useCallback(() => {
    setIsNotificationsOpen(!isNotificationsOpen);
  }, [isNotificationsOpen]);

  const [isDomicileSelectorOpen, setIsDomicileSelectorOpen] = useState<boolean>(false);
  const onToggleDomicileSelector = useCallback(() => {
    setIsDomicileSelectorOpen(!isDomicileSelectorOpen);
  }, [setIsDomicileSelectorOpen, isDomicileSelectorOpen]);

  const onNavigateToPortfoliosPage: MouseEventHandler<HTMLAnchorElement> = useCallback(
    (e) => {
      e.preventDefault();
      navigate(getURL('/portfolios'));
    },
    [getURL],
  );

  const onNavigateToAssetsPage: MouseEventHandler<HTMLAnchorElement> = useCallback(
    (e) => {
      e.preventDefault();
      navigate(getURL('/assets'));
    },
    [getURL],
  );

  const content =
    parameters?.status?.level === 'unavailable' ? (
      <PageErrorState
        title="Cannot connect to the server"
        content={
          <p>
            The <strong>AZByte | ETF</strong> server is temporary not available.
          </p>
        }
      />
    ) : (
      <Outlet />
    );

  const settingsFlyout = isSettingsOpen ? <SettingsFlyout onClose={onToggleSettings} /> : null;
  const notificationsFlyout = isNotificationsOpen ? (
    <NotificationsFlyout onClose={onToggleNotifications} notifications={notifications.notifications} />
  ) : null;
  const domicileSelectorModal = isDomicileSelectorOpen ? (
    <DomicileSelectorModal onClose={onToggleDomicileSelector} />
  ) : null;

  const onLogoClick = useCallback(() => {
    navigate(getURL('/'));
  }, [getURL]);

  const onNavigatePrivacyPolicy: MouseEventHandler<HTMLAnchorElement> = useCallback(
    (e) => {
      e.preventDefault();

      navigate(getURL('/privacy'));
    },
    [getURL],
  );

  const onNavigateTerms: MouseEventHandler<HTMLAnchorElement> = useCallback(
    (e) => {
      e.preventDefault();

      navigate(getURL('/terms'));
    },
    [getURL],
  );

  const onNavigateAboutUs: MouseEventHandler<HTMLAnchorElement> = useCallback(
    (e) => {
      e.preventDefault();

      navigate(getURL('/about-us'));
    },
    [getURL],
  );

  const [isContactFormOpen, setIsContactFormOpen] = useState<boolean>(false);
  const onToggleContactForm = useCallback(() => {
    setIsContactFormOpen(!isContactFormOpen);
  }, [isContactFormOpen, setIsContactFormOpen]);

  const contactFormModal = isContactFormOpen ? <ContactFormModal onClose={onToggleContactForm} /> : null;
  const onContactForm: MouseEventHandler<HTMLAnchorElement> = useCallback(
    (e) => {
      e.preventDefault();
      onToggleContactForm();
    },
    [onToggleContactForm],
  );

  const [isSunsetModelOpen, setIsSunsetModelOpen] = useState<boolean>(true);
  const sunsetModal = isSunsetModelOpen ? (
    <SunsetModal onClose={() => setIsSunsetModelOpen(!isSunsetModelOpen)} />
  ) : null;

  const pageTitle = useMemo<string | undefined>(() => {
    if (location.pathname.includes('/about-us')) {
      return 'About Us';
    }

    if (location.pathname.includes('/privacy')) {
      return 'Privacy Policy';
    }

    if (location.pathname.includes('/terms')) {
      return 'Terms of Use';
    }

    if (location.pathname.includes('/assets')) {
      return 'Assets';
    }

    if (location.pathname.includes('/alerts')) {
      return 'Alerts';
    }

    if (location.pathname.includes('/analyze')) {
      return 'Analyze';
    }

    if (location.pathname.includes('/service')) {
      return 'Service';
    }

    if (location.pathname.includes('/portfolios')) {
      return 'Portfolios';
    }
  }, [location]);

  const breadcrumbs = useMemo<EuiBreadcrumb[]>(
    () =>
      pageTitle
        ? [
            {
              text: 'Home',
              onClick: (e) => {
                e.preventDefault();
                navigate(getURL('/'));
              },
              href: getURL('/'),
            },
            { text: pageTitle },
          ]
        : [{ text: 'Home' }],
    [pageTitle, getURL],
  );

  const notificationsButton = useMemo(() => {
    const mainButton = (
      <EuiButtonIcon
        aria-label={'Notifications'}
        size={'m'}
        display={'empty'}
        color={'text'}
        iconType="bell"
        title={'List of your recent notifications'}
        onClick={onToggleNotifications}
      />
    );

    const hasUnReadNotifications =
      notifications.synced &&
      notifications.notifications.some((notification) => !notificationStatuses.has(notification.id));
    if (!hasUnReadNotifications) {
      return mainButton;
    }

    return (
      <div style={{ position: 'relative' }}>
        <EuiIcon
          css={css`
            position: absolute;
            top: 0;
            right: 0;
            stroke: #1d1e24;
          `}
          color="danger"
          type="dot"
          size="m"
        />
        {mainButton}
      </div>
    );
  }, [notifications, notificationStatuses, onToggleNotifications]);

  return (
    <EuiProvider colorMode={settings.theme}>
      <PageContext.Provider value={{ settings, setSettings, parameters, domicileCode, getURL, addToast }}>
        <EuiPage
          css={css`
            min-height: 100vh;
          `}
          restrictWidth="1500px"
        >
          <EuiPageBody>
            <EuiPageHeader
              pageTitle={
                <EuiFlexGroup>
                  <EuiFlexItem grow={false}>
                    <svg
                      style={{ margin: 'auto' }}
                      onClick={onLogoClick}
                      aria-labelledby="uniqueTitleID"
                      role="img"
                      width="216.02"
                      height="35.919"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <title id="uniqueTitleID">{pageTitle ? `AZbyte | ETF - ${pageTitle}` : 'AZbyte | ETF'}</title>
                      <g fill="#d46969">
                        <path d="M21.43 27.388h-4.198L10.766 8.7 4.232 27.388H0L8.735 2.471h4.028zM13 19.466l.982 2.742H7.516l.948-2.742zM42.792 2.438V5.72L30.909 23.562l-.677.542h12.56v3.284h-16.59v-3.284l11.884-17.84.71-.543H26.204V2.437zM48.31 1.083h4.096V25.12q-.033.474-.101 1.084-.068.609-.102 1.185H48.31zm16.69 13q.406 1.558.406 3.42 0 1.93-.474 3.757-.44 1.828-1.388 3.25-.914 1.388-2.336 2.235-1.388.812-3.284.812-1.184 0-2.268-.27-1.083-.271-1.862-.745V23.02q.508.575 1.185.948.711.372 1.557.372 1.016 0 1.829-.508.846-.507 1.421-1.354.61-.88.948-2.065.373-1.185.373-2.539 0-1.32-.339-2.505-.304-1.185-.88-2.1-.542-.913-1.354-1.455-.813-.542-1.828-.542-.88 0-1.625.373-.711.338-1.287 1.015V9.04q.779-.44 1.727-.677.948-.237 1.963-.237 1.118 0 2.269.305 1.15.304 2.132 1.015 1.016.711 1.828 1.862.847 1.151 1.287 2.776zM88.427 8.294q-1.862 4.977-3.318 8.836-1.455 3.826-2.539 6.737-1.083 2.878-1.93 4.875-.812 1.998-1.523 3.318-.677 1.354-1.286 2.099-.61.779-1.253 1.185-.643.406-1.422.474-.745.101-1.726.101-.305 0-.711-.101-.407-.068-.813-.203-.372-.102-.677-.237l-.406-.136q.135-.61.27-1.15.102-.475.204-.915.135-.406.203-.575.27 0 .812.169.576.17.847.17.541 0 .948-.069.44-.033.778-.304.339-.237.643-.745.305-.474.678-1.388l1.286-3.047 6.805-19.094zm-11.646 17.3-7.11-17.3h4.097l4.875 12.831-.474 1.151zM98.211 21.599q0 1.151.305 1.794.304.644.846.982.576.339 1.253.44.71.068 1.49.068v2.505q-.44.068-.881.102-.372.033-.914.033-.508.034-1.016.034t-1.05-.034q-.507-.033-.88-.101-1.15-.203-1.794-.982-.643-.812-.981-1.76-.305-.982-.407-1.896-.067-.914-.067-1.422V11.07h-2.506V8.294h2.506V2.877h4.096v5.417h3.047v2.776H98.21zM115.78 8.125q1.828.17 3.284.982 1.49.812 2.539 2.099 1.05 1.286 1.591 2.945.576 1.659.576 3.453 0 .813-.102 1.625h-11.612V16.25h7.82q0-.88-.304-1.693-.271-.812-.847-1.49-.541-.71-1.286-1.184t-1.659-.576zm-.135 16.081q.372 0 .914-.068.575-.101 1.185-.304.643-.237 1.252-.576.643-.338 1.117-.812l1.49 2.268q-1.117 1.388-2.607 2.099-1.49.71-3.792.71-1.862 0-3.487-.744-1.625-.779-2.776-2.1-1.15-1.32-1.828-3.08-.677-1.76-.677-3.791 0-1.93.61-3.657.643-1.726 1.726-3.013 1.084-1.32 2.505-2.133 1.456-.812 3.115-.914v3.25q-1.253.237-2.065 1.016-.813.779-1.287 1.794-.44.982-.643 2.032-.17 1.05-.17 1.794 0 1.32.306 2.471.338 1.117 1.015 1.964.677.812 1.659 1.32 1.016.474 2.438.474zM141.41 35.581h-2.742V0h2.742z" />
                        <g fill="#6092c0">
                          <path d="M156.71 2.438h4.13v24.95h-4.13zm14.557 13.71h-9.04v-2.776h9.04zm-9.04 8.16h10.09v3.08h-10.09zm10.09-21.87v3.08h-10.09v-3.08zM195.47 2.438v2.775h-7.448v22.174h-4.096V5.213h-7.448V2.438zM200.42 2.438h4.13v24.95h-4.13zm14.557 13.71h-9.04v-2.776h9.04zm1.05-13.71v3.08h-10.09v-3.08z" />
                        </g>
                      </g>
                    </svg>
                  </EuiFlexItem>
                  <EuiFlexItem>
                    <EuiHeaderBreadcrumbs aria-label="Navigation menu" breadcrumbs={breadcrumbs} responsive={false} />
                  </EuiFlexItem>
                </EuiFlexGroup>
              }
              paddingSize={'none'}
              alignItems={'center'}
              rightSideGroupProps={{ gutterSize: 'xs', justifyContent: 'center', direction: 'rowReverse' }}
              rightSideItems={[
                <EuiButtonIcon
                  aria-label={'Settings'}
                  size={'m'}
                  display={'empty'}
                  color={'text'}
                  iconType="gear"
                  title={'Change your settings'}
                  onClick={onToggleSettings}
                />,
                notificationsButton,
                <EuiButtonIcon
                  aria-label={'Stocks, Bonds, and Other Securities'}
                  size={'m'}
                  display={'empty'}
                  color={'text'}
                  iconType="visTable"
                  title={'Explore stocks, bonds, and other securities'}
                  href={getURL('/assets')}
                  onClick={onNavigateToAssetsPage}
                />,
                <EuiButtonIcon
                  aria-label={'Portfolios'}
                  size={'m'}
                  display={'empty'}
                  color={'text'}
                  iconType="submodule"
                  href={getURL('/portfolios')}
                  onClick={onNavigateToPortfoliosPage}
                  title={'Portfolios'}
                />,
                <EuiButtonEmpty
                  aria-label={'Domicile'}
                  iconType="mapMarker"
                  color="primary"
                  onClick={onToggleDomicileSelector}
                  title={'Set your domicile'}
                >
                  {getDomicileLabel(parameters)}
                </EuiButtonEmpty>,
              ]}
            />
            <EuiPageContent hasShadow={false}>
              <EuiFlexGroup style={{ height: '100%' }} direction={'column'} gutterSize={'none'}>
                <EuiFlexItem>
                  <EuiPageContentBody style={{ height: '100%' }}>{content}</EuiPageContentBody>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiHorizontalRule size={'half'} />
                  <EuiText textAlign={'center'} size={'s'}>
                    <EuiLink href={getURL('/about-us')} onClick={onNavigateAboutUs} color={'success'}>
                      About Us
                    </EuiLink>{' '}
                    |{' '}
                    <EuiLink href={getURL('/privacy')} onClick={onNavigatePrivacyPolicy} color={'success'}>
                      Privacy Policy
                    </EuiLink>{' '}
                    |{' '}
                    <EuiLink href={getURL('/terms')} onClick={onNavigateTerms} color={'success'}>
                      Terms of Use
                    </EuiLink>{' '}
                    |{' '}
                    <EuiLink onClick={onContactForm} color={'success'}>
                      Contact Us
                    </EuiLink>
                  </EuiText>
                  <EuiSpacer size={'s'} />
                  <EuiText size={'xs'} color={'subdued'} textAlign={'center'}>
                    This page is not an investment advice and does not constitute any offer or solicitation to offer or
                    recommendation of any any investment product. There is no warranty for completeness, accuracy and
                    correctness for the displayed information. The basic stock information is sourced from{' '}
                    <EuiLink href={getURL('/terms#stocks-data')} target={'_blank'}>
                      Investing.com
                    </EuiLink>
                    . ETF details and holdings are sourced directly from the{' '}
                    <EuiLink href={getURL('/terms#providers-data')} target={'_blank'}>
                      respective fund providers
                    </EuiLink>
                    .
                  </EuiText>
                </EuiFlexItem>
              </EuiFlexGroup>
              <EuiGlobalToastList toasts={toasts} dismissToast={removeToast} toastLifeTimeMs={5000} />
            </EuiPageContent>
          </EuiPageBody>
          {settingsFlyout}
          {notificationsFlyout}
          {domicileSelectorModal}
          {contactFormModal}
          {sunsetModal}
        </EuiPage>
      </PageContext.Provider>
    </EuiProvider>
  );
}
